import React from "react";
import { Modal, Typography, Button, Divider, Tag, List } from "antd";
import { ClockCircleOutlined, CloseOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { formatStatusWithActor } from "../../../../utils/ouberUtils";

const { Text, Title } = Typography;

const getPassengerName = (m) => {
  if (m.employee) {
    return `${m.employee.first_name || ""} ${m.employee.last_name || ""}`.trim() || "Unknown";
  }
  return m.external_passenger_name || "Guest";
};

const UserViewTripReqModal = ({ isOpen, onClose, requestData, onCancelRequest }) => {
  const currentUserId = useSelector((state) => state.accountDetailSlice?.details?.id);

  if (!requestData) return null;

  const isLinked = !!requestData.trip;
  const headerBgColor = "#862633";

  // group_members now includes this row itself when the request is part of
  // a group (see backend comment on TripRequest::groupMembers()); fall back
  // to just this row for a solo (non-grouped) request.
  const passengers = (requestData.group_members && requestData.group_members.length > 0)
    ? [...requestData.group_members].sort((a, b) => a.trip_request_id - b.trip_request_id)
    : [requestData];

  // Once the linked trip is already underway or finished, the requester
  // can no longer back out — the driver/vehicle may already be committed.
  const linkedTripStatus = requestData.trip?.trip_status || requestData.trip?.status;
  const isTripLockedForCancel = ["In Progress", "Completed Trip", "Completed"].includes(linkedTripStatus);

  const renderRequestDetails = () => (
    <div style={{ marginTop: '16px' }}>
      <Title level={5} style={{ margin: 0, marginBottom: '12px' }}>Your Request Details</Title>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div>
          <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Requested Route</Text>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Text>{requestData.pickup_venue || requestData.pickup || "N/A"}</Text> 
            <ArrowRightOutlined style={{ fontSize: '10px', color: '#8c8c8c' }} /> 
            <Text>{requestData.dropoff_location || requestData.dest || requestData.destination || "N/A"}</Text>
          </div>
        </div>
        <div>
          <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Requested Date</Text>
          <Text>{requestData.trip_date ? dayjs(requestData.trip_date).format("MMMM D, YYYY") : (requestData.date ? dayjs(requestData.date).format("MMMM D, YYYY") : "N/A")}</Text>
        </div>
        <div>
          <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Preferred Pickup Time</Text>
          <Text>
            <ClockCircleOutlined style={{ marginRight: '4px' }} /> 
            {requestData.estimated_pickup_time ? dayjs(requestData.estimated_pickup_time, "HH:mm:ss").format("h:mm A") : (requestData.time || "Requested to Join")}
          </Text>
        </div>
        <div>
          <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Pickup Venue</Text>
          <Text>{requestData.pickup_venue || requestData.pickup || "N/A"}</Text>
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Purpose of Trip</Text>
          <Text>{requestData.trip_purpose || requestData.purpose || "Official Business"}</Text>
        </div>
      </div>

      <Divider style={{ margin: '16px 0 12px' }} />

      <Text type="secondary" style={{ display: 'block', fontSize: '12px', marginBottom: '8px' }}>
        Passengers ({passengers.length})
      </Text>
      <List
        size="small"
        bordered
        dataSource={passengers}
        rowKey={(m) => m.trip_request_id || m.id}
        renderItem={(m) => {
          const isLeader = m.trip_request_id === m.request_group_id;
          const isYou = !!currentUserId && m.employee_id && m.employee_id === currentUserId;
          return (
            <List.Item>
              <Text>{getPassengerName(m)}</Text>
              <span style={{ marginLeft: '8px' }}>
                {isLeader && <Tag color="blue" style={{ fontSize: '10px' }}>Head of the Party</Tag>}
                {isYou && <Tag style={{ fontSize: '10px' }}>You</Tag>}
                {!m.employee_id && <Tag color="orange" style={{ fontSize: '10px' }}>Guest</Tag>}
              </span>
            </List.Item>
          );
        }}
      />
    </div>
  );

  const renderTripDetails = () => {
    const trip = requestData.trip;
    if (!trip) return null;

    const formatTime = (timeStr) => {
      if (!timeStr || timeStr === 'N/A') return 'TBD';
      return dayjs(timeStr, "HH:mm:ss").format("h:mm A");
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Title level={5} style={{ margin: 0 }}>Trip Details</Title>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Route</Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Text>{trip.pickup_venue}</Text> 
              <ArrowRightOutlined style={{ fontSize: '10px', color: '#8c8c8c' }} /> 
              <Text>{trip.dropoff_location}</Text>
            </div>
          </div>
          <div>
            <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Date</Text>
            <Text>{dayjs(trip.trip_date).format("MMMM D, YYYY")}</Text>
          </div>
          <div>
            <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Driver</Text>
            <Text>{(trip.driver && typeof trip.driver === 'object' && trip.driver.driver_name) ? trip.driver.driver_name : (trip.custom_driver_name || "Unassigned")}</Text>
          </div>
          <div>
            <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Contact Number</Text>
            <Text>{(trip.driver && typeof trip.driver === 'object' && trip.driver.contact_number) ? trip.driver.contact_number : (trip.custom_driver_contact || "N/A")}</Text>
          </div>
          <div>
            <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Pickup Time</Text>
            <Text>{formatTime(trip.pickup_time)}</Text>
          </div>
          <div>
            <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Estimated Arrival</Text>
            <Text>{formatTime(trip.estimated_arrival_time)}</Text>
          </div>
          <div>
            <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Vehicle</Text>
            <Text>{(trip.vehicle && typeof trip.vehicle === 'object' && trip.vehicle.vehicle_model) ? trip.vehicle.vehicle_model : (trip.custom_vehicle_name || "Unassigned")}</Text>
          </div>
          <div>
            <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Plate Number</Text>
            <Text>{(trip.vehicle && typeof trip.vehicle === 'object' && trip.vehicle.plate_number) ? trip.vehicle.plate_number : (trip.custom_vehicle_plate || "N/A")}</Text>
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Purpose of Trip</Text>
            <Text>{trip.trip_purpose || "Official Business"}</Text>
          </div>
          <div>
            <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Capacity</Text>
            <Text>{trip.passengers ? trip.passengers.length : 0}/{trip.trip_passenger_capacity || "N/A"}</Text>
          </div>
        </div>
      </div>
    );
  };

  const currentStatus = requestData.request_status || requestData.status;

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={600}
      closeIcon={<CloseOutlined style={{ color: "white" }} />}
      styles={{
        header: {
          backgroundColor: headerBgColor,
          padding: "16px 24px",
          borderRadius: "8px 8px 0 0",
          margin: "-20px -24px 24px -24px"
        },
        title: { color: "white", margin: 0 },
        body: { maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden', margin: '0 -24px', padding: '0 24px' }
      }}
      title={<Title level={5} style={{ color: 'white', margin: 0, fontSize: '16px' }}>View Trip Request</Title>}
      className="white-close-icon"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        
        {/* Status Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f9f9f9', padding: '12px', borderRadius: '8px', marginBottom: '8px' }}>
          <div>
            <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Date Submitted</Text>
            <Text strong>{requestData.created_at ? dayjs(requestData.created_at).format('MMMM D, YYYY') : dayjs(requestData.date).format('MMMM D, YYYY')}</Text>
          </div>
          <div style={{ textAlign: 'right' }}>
            <Text type="secondary" style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>Status</Text>
             <Tag 
                className={
                   currentStatus === "Approved by AO" || currentStatus === "Created by AO" ? "tag-approved" :
                   currentStatus === "Completed Trip" ? "tag-completed" :
                   currentStatus === "Requested to Join" ? "tag-pending" : "tag-cancelled"
                }
                style={{ borderRadius: "12px", minWidth: "80px", textAlign: "center", border: 'none', margin: 0 }}
             >
                {formatStatusWithActor(currentStatus, requestData.status_changed_by)}
             </Tag>
          </div>
        </div>

        {isLinked ? (
          <>
            {renderTripDetails()}
            {(() => {
              const trip = requestData.trip;
              
              const isMultipleAllowed = !!(
                trip.isMultiplePickupAllowed || 
                trip.allowed_multiple_pickup === 1 || 
                trip.allowed_multiple_pickup === true ||
                trip.allowed_multiple_pickup === "1"
              );

              if (!isMultipleAllowed) return null;

              const formatTime = (t) => {
                if (!t) return null;
                return dayjs(t, "HH:mm:ss").format("h:mm A");
              };
              
              const pTime = requestData.estimated_pickup_time ? dayjs(requestData.estimated_pickup_time, "HH:mm:ss").format("h:mm A") : (requestData.time || "Requested to Join");
              const tTime = formatTime(trip.pickup_time);
              const pVenue = (requestData.pickup_venue || requestData.pickup || "").toString().trim();
              const tVenue = (trip.pickup_venue || "").toString().trim();
              
              const isDiff = (pTime !== tTime && pTime !== "Requested to Join") || (pVenue !== tVenue && pVenue !== "");

              if (!isDiff) return null;

              return (
                <>
                  <Divider style={{ margin: '12px 0' }} />
                  <div style={{ marginTop: '8px' }}>
                    <Text type="secondary" style={{ display: 'block', fontSize: '11px', marginBottom: '8px' }}>Your Custom Pickup Request</Text>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                       {pTime !== tTime && (
                         <div>
                            <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Preferred Time</Text>
                            <Text strong><ClockCircleOutlined style={{ marginRight: '4px' }} /> {pTime}</Text>
                         </div>
                       )}
                       {pVenue !== tVenue && (
                         <div>
                            <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Preferred Venue</Text>
                            <Text strong>{pVenue}</Text>
                         </div>
                       )}
                    </div>
                  </div>
                </>
              );
            })()}
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
              <Button onClick={onClose}>Close</Button>
              {(currentStatus === "Requested to Join" || currentStatus === "Approved by AO") && !isTripLockedForCancel && (
                <Button className="ouber-btn-danger" onClick={() => onCancelRequest(requestData)}>
                  Cancel Request
                </Button>
              )}
            </div>
          </>
        ) : (
          <>
             <div style={{ backgroundColor: '#fffbe6', padding: '12px', borderRadius: '8px', borderLeft: '4px solid #faad14', marginBottom: '8px' }}>
               <Text type="warning" style={{ fontSize: '13px', color: '#d46b08' }}>
                 <strong>Note:</strong> This request does not have an assigned trip. Wait for further updates from the administrative officer.
               </Text>
             </div>
             {renderRequestDetails()}
             
             <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px', borderTop: '1px solid #f0f0f0', paddingTop: '16px' }}>
              <Button onClick={onClose}>Close</Button>
              {(currentStatus === "Requested to Join" || currentStatus === "Approved by AO") && !isTripLockedForCancel && (
                <Button className="ouber-btn-danger" onClick={() => onCancelRequest(requestData)}>
                  Cancel Request
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default UserViewTripReqModal;

